import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { adminCoupons, type CouponOut, type CouponCreate } from '../../api/admin';
import {
  Ticket, Plus, Trash2, X, Check, Copy, Pencil,
  ToggleLeft, ToggleRight, Calendar, Percent, IndianRupee,
} from 'lucide-react';

// Coupon dates are stored as UTC in the DB but admins enter / expect IST
// (the only timezone this storefront serves). The two helpers below
// translate between the form's wall-clock "YYYY-MM-DDTHH:mm" string and
// the API's UTC ISO. Without them, "31 May 11:59 PM" typed in the form
// gets stored as 11:59 PM UTC (= 5:29 AM IST 1 June), making coupons
// outlive their intended IST expiry by ~5.5 hours.
const IST_OFFSET_MIN = 330; // IST is UTC+5:30

// API UTC ISO → "YYYY-MM-DDTHH:mm" expressing IST wall-clock for the
// <input type="datetime-local"> field.
function utcIsoToIstLocal(utcIso: string | null | undefined): string | null {
  if (!utcIso) return null;
  const d = new Date(utcIso);
  if (isNaN(d.getTime())) return null;
  const ist = new Date(d.getTime() + IST_OFFSET_MIN * 60_000);
  return ist.toISOString().slice(0, 16);
}

// Form wall-clock (IST) "YYYY-MM-DDTHH:mm" → UTC ISO for the API.
function istLocalToUtcIso(istLocal: string | null | undefined): string | null {
  if (!istLocal) return null;
  const asIfUtc = new Date(istLocal + 'Z');
  if (isNaN(asIfUtc.getTime())) return null;
  return new Date(asIfUtc.getTime() - IST_OFFSET_MIN * 60_000).toISOString();
}

const empty: CouponCreate = {
  code: '',
  discount_type: 'percentage',
  discount_value: 10,
  min_order_value: 0,
  max_discount: null,
  usage_limit: null,
  valid_from: null,
  valid_until: null,
  is_active: true,
};

export default function CouponsPage() {
  const { isDark } = useTheme();
  const [coupons, setCoupons] = useState<CouponOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CouponCreate>({ ...empty });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminCoupons.list().then(setCoupons).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: CouponCreate = {
        ...form,
        code: form.code.toUpperCase(),
        valid_from: istLocalToUtcIso(form.valid_from),
        valid_until: istLocalToUtcIso(form.valid_until),
      };
      if (editingId) {
        const updated = await adminCoupons.update(editingId, payload);
        setCoupons(prev => prev.map(c => c.id === editingId ? updated : c));
      } else {
        const created = await adminCoupons.create(payload);
        setCoupons(prev => [...prev, created]);
      }
      setForm({ ...empty });
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save coupon';
      setError(msg);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c: CouponOut) => {
    setForm({
      code: c.code,
      discount_type: c.discount_type,
      discount_value: c.discount_value,
      min_order_value: c.min_order_value,
      max_discount: c.max_discount,
      usage_limit: c.usage_limit,
      valid_from: utcIsoToIstLocal(c.valid_from),
      valid_until: utcIsoToIstLocal(c.valid_until),
      is_active: c.is_active,
    });
    setEditingId(c.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async (coupon: CouponOut) => {
    try {
      const updated = await adminCoupons.update(coupon.id, { is_active: !coupon.is_active });
      setCoupons(prev => prev.map(c => c.id === coupon.id ? updated : c));
    } catch (err) { console.error(err); }
  };

  const deleteCoupon = async (coupon: CouponOut) => {
    if (!confirm(`Delete coupon "${coupon.code}"?`)) return;
    try {
      await adminCoupons.delete(coupon.id);
      setCoupons(prev => prev.filter(c => c.id !== coupon.id));
    } catch (err) { console.error(err); }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const active = coupons.filter(c => c.is_active);
  const inactive = coupons.filter(c => !c.is_active);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Ticket size={24} /> Coupons</h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {active.length} active &middot; {inactive.length} inactive
          </p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); if (showForm) { setEditingId(null); setForm({ ...empty }); } }}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {showForm ? <X size={16} /> : <Plus size={16} />}
          {showForm ? 'Cancel' : 'New Coupon'}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleSubmit} className={`rounded-xl border p-6 space-y-4 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <h2 className="font-semibold text-lg">{editingId ? 'Edit Coupon' : 'Create Coupon'}</h2>
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">{error}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Code</label>
              <input
                type="text"
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="SUMMER20"
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none font-mono
                  ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
                required
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Type</label>
              <select
                value={form.discount_type}
                onChange={e => setForm({ ...form, discount_type: e.target.value as 'percentage' | 'fixed' })}
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none
                  ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Value {form.discount_type === 'percentage' ? '(%)' : '(₹)'}
              </label>
              <input
                type="number"
                value={form.discount_value}
                onChange={e => setForm({ ...form, discount_value: Number(e.target.value) })}
                min={1}
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none
                  ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
                required
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Min Order (₹)</label>
              <input
                type="number"
                value={form.min_order_value || ''}
                onChange={e => setForm({ ...form, min_order_value: Number(e.target.value) || 0 })}
                placeholder="0"
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none
                  ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Max Discount (₹)</label>
              <input
                type="number"
                value={form.max_discount ?? ''}
                onChange={e => setForm({ ...form, max_discount: e.target.value ? Number(e.target.value) : null })}
                placeholder="No limit"
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none
                  ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Usage Limit</label>
              <input
                type="number"
                value={form.usage_limit ?? ''}
                onChange={e => setForm({ ...form, usage_limit: e.target.value ? Number(e.target.value) : null })}
                placeholder="Unlimited"
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none
                  ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Valid From (IST)</label>
              <input
                type="datetime-local"
                value={form.valid_from ?? ''}
                onChange={e => setForm({ ...form, valid_from: e.target.value || null })}
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none
                  ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Valid Until (IST)</label>
              <input
                type="datetime-local"
                value={form.valid_until ?? ''}
                onChange={e => setForm({ ...form, valid_until: e.target.value || null })}
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none
                  ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Check size={16} /> {saving ? (editingId ? 'Saving...' : 'Creating...') : (editingId ? 'Save Changes' : 'Create Coupon')}
          </button>
        </form>
      )}

      {/* Coupons grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {coupons.length === 0 && (
          <div className={`col-span-full text-center py-12 rounded-xl border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            <Ticket size={40} className={`mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No coupons yet. Create your first one!</p>
          </div>
        )}
        {coupons.map(c => (
          <div key={c.id} className={`rounded-xl border p-5 transition-shadow hover:shadow-md ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} ${!c.is_active ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`font-mono font-bold text-lg ${c.is_active ? 'text-emerald-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {c.code}
                </span>
                <button onClick={() => copyCode(c.code)} className="text-gray-400 hover:text-gray-300 transition-colors" title="Copy code">
                  {copied === c.code ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                </button>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => startEdit(c)} className="p-1 text-blue-400 hover:bg-blue-500/10 rounded transition-colors" title="Edit">
                  <Pencil size={15} />
                </button>
                <button onClick={() => toggleActive(c)} title={c.is_active ? 'Deactivate' : 'Activate'}>
                  {c.is_active ? <ToggleRight size={22} className="text-emerald-500" /> : <ToggleLeft size={22} className="text-gray-400" />}
                </button>
                <button onClick={() => deleteCoupon(c)} className="p-1 text-red-400 hover:bg-red-500/10 rounded transition-colors" title="Delete">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              {c.discount_type === 'percentage' ? (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded-full text-xs font-medium">
                  <Percent size={12} /> {c.discount_value}% off
                </span>
              ) : (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-500 rounded-full text-xs font-medium">
                  <IndianRupee size={12} /> {fmt(c.discount_value)} off
                </span>
              )}
              {c.max_discount && (
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  max {fmt(c.max_discount)}
                </span>
              )}
            </div>

            <div className={`space-y-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {c.min_order_value > 0 && <p>Min order: {fmt(c.min_order_value)}</p>}
              {c.usage_limit && <p>Usage: {c.used_count} / {c.usage_limit}</p>}
              {!c.usage_limit && <p>Usage: {c.used_count} (unlimited)</p>}
              {(c.valid_from || c.valid_until) && (
                <p className="flex items-center gap-1">
                  <Calendar size={11} />
                  {c.valid_from ? new Date(c.valid_from).toLocaleDateString('en-IN') : 'Any'} –{' '}
                  {c.valid_until ? new Date(c.valid_until).toLocaleDateString('en-IN') : 'No expiry'}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
